import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Loader2, Lock } from "lucide-react";
import { API } from "@/lib/api";

/**
 * PasswordGate — private-beta wall shown before Google OAuth.
 *
 * On mount: GET /api/access/status → if granted, fade out and call onGranted().
 * On submit: POST /api/access/verify → set signed cookie server-side, then
 * onGranted(). The password itself never touches localStorage.
 *
 * Props:
 *   onGranted(): fired when the caller can proceed to the normal app tree.
 */
export default function PasswordGate({ onGranted }) {
  const [phase, setPhase] = useState("checking"); // checking | prompt | granting | out
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  // Initial status check — if the cookie is still valid we skip the form.
  // Fast path: an optimistic `hm_gate_hint` set after a prior successful
  // POST /verify in this same browser session lets us skip the fetch and
  // its spinner entirely — the backend cookie is still the source of truth
  // (and the gate middleware re-verifies on every API call), the hint is
  // purely a UX accelerator to survive the full-page remount after Google
  // OAuth redirect.
  //
  // Slow path: `/api/access/status` with an 8 s AbortController timeout
  // (covers Render free-tier cold starts behind the Vercel rewrite proxy).
  useEffect(() => {
    // --- Fast path ---
    try {
      const raw = sessionStorage.getItem("hm_gate_hint");
      if (raw) {
        const { exp } = JSON.parse(raw);
        if (exp && Date.now() < exp * 1000) {
          onGranted();
          return;
        }
        sessionStorage.removeItem("hm_gate_hint");
      }
    } catch {
      try { sessionStorage.removeItem("hm_gate_hint"); } catch { /* ignore */ }
    }

    // --- Slow path ---
    let alive = true;
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 8000);
    (async () => {
      try {
        const r = await fetch(`${API}/access/status`, {
          credentials: "include",
          signal: controller.signal,
        });
        if (!alive) return;
        if (r.ok) {
          const d = await r.json();
          if (d.granted) {
            // Refresh the hint from the authoritative response.
            if (d.expires_at) {
              try {
                sessionStorage.setItem("hm_gate_hint", JSON.stringify({ exp: d.expires_at }));
              } catch { /* storage full / disabled */ }
            }
            setPhase("out");
            setTimeout(() => alive && onGranted(), 320);
            return;
          }
          // Authoritative "not granted" → drop any stale hint.
          try { sessionStorage.removeItem("hm_gate_hint"); } catch { /* ignore */ }
        }
      } catch { /* AbortError or network → fall through to prompt */ }
      finally { clearTimeout(t); }
      if (alive) {
        setPhase("prompt");
        setTimeout(() => inputRef.current?.focus(), 60);
      }
    })();
    return () => { alive = false; controller.abort(); clearTimeout(t); };
  }, [onGranted]);

  const submit = async (e) => {
    e?.preventDefault();
    if (busy || !password) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API}/access/verify`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (r.ok) {
        // Persist an optimistic hint so a full-page remount (e.g. Google
        // OAuth redirect) short-circuits the slow /status round-trip.
        try {
          const d = await r.json();
          if (d && d.expires_at) {
            sessionStorage.setItem("hm_gate_hint", JSON.stringify({ exp: d.expires_at }));
          }
        } catch { /* body not JSON — hint is a nice-to-have, ignore */ }
        setPhase("out");
        setTimeout(onGranted, 320);
        return;
      }
      // Generic message — never surface "rate limited" specifically to
      // avoid leaking bruteforce state.
      let msg = "Invalid access code. Please try again.";
      if (r.status === 429) {
        msg = "Too many attempts. Please wait a few minutes and try again.";
      }
      setError(msg);
      setShake(true);
      setPassword("");
      setTimeout(() => setShake(false), 400);
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch {
      setError("Network error. Please check your connection.");
      setShake(true);
      setTimeout(() => setShake(false), 400);
    } finally {
      setBusy(false);
    }
  };

  // While checking, render a plain background so we don't flash the form.
  if (phase === "checking") {
    return (
      <div
        style={{
          position: "fixed", inset: 0, background: "#F7FAFC",
          display: "grid", placeItems: "center", zIndex: 60,
        }}
        data-testid="access-gate-checking"
      >
        <Loader2 className="animate-spin" style={{ color: "#5B6B82", width: 24, height: 24 }} />
      </div>
    );
  }

  const fadingOut = phase === "out";

  return (
    <>
      <style>{`
        @keyframes hm-gate-shake {
          0%,100% { transform: translateX(0); }
          20%     { transform: translateX(-6px); }
          40%     { transform: translateX(6px); }
          60%     { transform: translateX(-4px); }
          80%     { transform: translateX(4px); }
        }
        .hm-gate-shake { animation: hm-gate-shake 380ms ease; }
      `}</style>
      <div
        data-testid="access-gate"
        style={{
          position: "fixed", inset: 0, zIndex: 60,
          background: "#F7FAFC",
          display: "grid", placeItems: "center",
          padding: 24,
          opacity: fadingOut ? 0 : 1,
          transition: "opacity 320ms ease",
          pointerEvents: fadingOut ? "none" : "auto",
        }}
      >
        <form
          onSubmit={submit}
          style={{
            width: "100%", maxWidth: 448,
            background: "#FFFFFF",
            border: "1px solid #EEF1F5",
            borderRadius: 16,
            boxShadow: "0 10px 40px rgba(15, 27, 45, 0.08)",
            padding: 40,
            display: "flex", flexDirection: "column", gap: 20,
          }}
          data-testid="access-gate-form"
          aria-labelledby="access-gate-title"
        >
          {/* Brand */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: 8,
                background: "#0FA9DB", color: "#fff",
                display: "grid", placeItems: "center",
              }}
              aria-hidden
            >
              <Lock style={{ width: 18, height: 18 }} strokeWidth={2.2} />
            </div>
            <div style={{ color: "#0F1B2D", fontSize: 22, fontWeight: 600, letterSpacing: -0.2 }}>
              Beyond Activ
            </div>
          </div>

          {/* Headings */}
          <div style={{ marginTop: 4 }}>
            <h1
              id="access-gate-title"
              style={{ color: "#0F1B2D", fontSize: 20, fontWeight: 600, margin: 0 }}
            >
              Private access
            </h1>
            <p style={{ color: "#3D4B62", fontSize: 14, marginTop: 6, lineHeight: 1.55 }}>
              Enter the platform access code to continue.
            </p>
          </div>

          {/* Password input */}
          <div style={{ position: "relative" }} className={shake ? "hm-gate-shake" : ""}>
            <input
              ref={inputRef}
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Access code"
              autoComplete="current-password"
              spellCheck={false}
              disabled={busy}
              aria-invalid={!!error}
              aria-describedby={error ? "access-gate-error" : undefined}
              data-testid="access-gate-input"
              style={{
                width: "100%", height: 48,
                padding: "0 44px 0 16px",
                borderRadius: 8,
                border: `1px solid ${error ? "#DC2626" : "#E5E9F0"}`,
                background: "#FFFFFF",
                color: "#0F1B2D",
                fontSize: 15,
                outline: "none",
                transition: "border-color 150ms ease, box-shadow 150ms ease",
              }}
              onFocus={(e) => {
                if (error) return;
                e.currentTarget.style.borderColor = "#0FA9DB";
                e.currentTarget.style.boxShadow = "0 0 0 4px rgba(15,169,219,0.14)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = "none";
                if (!error) e.currentTarget.style.borderColor = "#E5E9F0";
              }}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "Hide password" : "Show password"}
              data-testid="access-gate-toggle"
              style={{
                position: "absolute", right: 8, top: 8,
                width: 32, height: 32, borderRadius: 6,
                display: "grid", placeItems: "center",
                color: "#5B6B82", background: "transparent", border: "none",
                cursor: "pointer",
              }}
            >
              {showPw
                ? <EyeOff style={{ width: 16, height: 16 }} />
                : <Eye style={{ width: 16, height: 16 }} />}
            </button>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={busy || !password}
            data-testid="access-gate-submit"
            style={{
              width: "100%", height: 48,
              borderRadius: 8, border: "none",
              background: busy || !password ? "#7FCAE4" : "#0FA9DB",
              color: "#FFFFFF", fontWeight: 600, fontSize: 15,
              cursor: busy || !password ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              transition: "background-color 150ms ease",
            }}
            onMouseEnter={(e) => { if (!busy && password) e.currentTarget.style.background = "#0B87AE"; }}
            onMouseLeave={(e) => { if (!busy && password) e.currentTarget.style.background = "#0FA9DB"; }}
          >
            {busy ? (
              <><Loader2 className="animate-spin" style={{ width: 16, height: 16 }} /> Verifying…</>
            ) : "Access platform"}
          </button>

          {/* Error */}
          {error && (
            <div
              id="access-gate-error"
              role="alert"
              data-testid="access-gate-error"
              style={{ color: "#DC2626", fontSize: 13, lineHeight: 1.5, marginTop: -8 }}
            >
              {error}
            </div>
          )}

          {/* Footer */}
          <div
            style={{
              marginTop: 8, paddingTop: 20,
              borderTop: "1px solid #EEF1F5",
              color: "#5B6B82", fontSize: 12, textAlign: "center",
              letterSpacing: 0.1,
            }}
          >
            Beyond Activ Intelligence · V1.0
          </div>
        </form>
      </div>
    </>
  );
}
